import { FlexRow } from '@/components/ui/flex';
import { useChat } from '@/services/ai';

import { useChatById } from '@/api/chat';
import { useScoreAnswerService } from '@/services/chat';
import { useChatLocation } from '@/utils/aiUtils';
import { type ChatContext } from '@ditto/shared';
import { Fragment, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Chat } from './components/Chat';

export const AgentChat: FC = () => {
  const { chat_id } = useParams();

  const { initialMessage } = useChatLocation();

  const navigate = useNavigate();

  const getChatContext = (): ChatContext => {
    return {
      id: chat_id ?? '',
    };
  };

  const { messages, sendMessage, status, regenerate, error, clearError, stop } =
    useChat(getChatContext, initialMessage);

  const { data: chatData } = useChatById(chat_id ?? '');

  const { scoreAnswer } = useScoreAnswerService();

  const lastMessage = messages[messages.length - 1];

  const isThinking =
    status === 'submitted' ||
    ((status === 'streaming' || status === 'ready') &&
      messages.length > 0 &&
      lastMessage?.parts.length <= 1);

  const isDisabled = status !== 'ready';

  const showStop = status === 'streaming' || status === 'submitted';

  const handleClose = () => {
    navigate(-1);
  };

  const handleErrorRegenerate = () => {
    clearError();
    regenerate();
  };

  const handleFeedback =
    (messageId: string) => (scoreValue: 'up' | 'down', comment?: string) => {
      scoreAnswer(
        {
          traceId: messageId,
          scoreValue,
          comment,
        },
        {
          onSuccess: () => {
            toast.success('Feedback sent successfully');
          },
          onError: () => {
            toast.error('Failed to send feedback');
          },
        }
      );
    };

  const placeholder = 'Ask a regulatory affairs question...';
  return (
    <Chat
      messages={messages}
      chatData={chatData ?? null}
      sendMessage={sendMessage}
    >
      <Chat.Header onClose={handleClose} />
      <FlexRow w="full" className="h-[calc(100%-65px)]">
        <Chat.DocumentPreview />
        <Chat.Layout>
          <Chat.Content>
            {messages.map((message, idx) => (
              <Fragment key={message.id}>
                {message.role === 'assistant' && (
                  <Chat.AiReasoningBlock
                    key={`${message.id}-${idx}`}
                    message={message}
                    isThinking={isThinking}
                    isLatestMessage={idx === messages.length - 1}
                  />
                )}
                <Chat.Message
                  key={message.id || idx}
                  message={message}
                  messageIndex={idx}
                  isLastMessage={idx === messages.length - 1}
                  onFeedback={handleFeedback(message.id)}
                />
              </Fragment>
            ))}
            {/** This is to show the loading as soon as the user sends a message */}
            {isThinking && lastMessage?.role === 'user' && (
              <Chat.AiReasoningBlock
                message={{
                  id: 'thinking',
                  role: 'assistant',
                  parts: [],
                }}
                isThinking={true}
                isLatestMessage
              />
            )}
            {error && (
              <Chat.ErrorMessage onRegenerate={handleErrorRegenerate} />
            )}
          </Chat.Content>
          <Chat.ScrollToBottomButton />
          <Chat.Input
            type="chat"
            onSubmit={sendMessage}
            disabled={isDisabled}
            onStop={showStop ? stop : undefined}
            placeholder={placeholder}
          />
        </Chat.Layout>
      </FlexRow>
    </Chat>
  );
};
